import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, User, KeyRound, FileCheck, LogOut } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtErr, inr, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const roCls = "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-600";

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", upi_id: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [subs, setSubs] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", upi_id: user.upi_id || "" });
  }, [user]);

  useEffect(() => {
    api.get("/user/submissions").then((r) => setSubs(r.data)).catch(() => setSubs([]));
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/user/profile", { ...form, email: user.email });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setSavingPw(true);
    try {
      await api.put("/user/password", pw);
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSavingPw(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="account-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Account</h1>
        <p className="mt-1 text-sm text-slate-600">Manage your profile, password and track your submissions.</p>
      </div>

      <form onSubmit={saveProfile} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-7 space-y-4" data-testid="profile-card">
        <div className="flex items-center gap-2.5">
          <User className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold font-heading text-slate-900">Profile Information</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">User ID</label>
            <input data-testid="profile-user-id" value={user.user_code} readOnly className={roCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Email</label>
            <input data-testid="profile-email" value={user.email} readOnly className={roCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Full name</label>
            <input data-testid="profile-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
            <input data-testid="profile-phone-input" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inCls} />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">UPI ID</label>
            <input data-testid="profile-upi-input" required value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className={inCls} placeholder="yourname@upi" />
          </div>
        </div>
        <button type="submit" data-testid="profile-save-btn" disabled={saving}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
        </button>
      </form>

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-7" data-testid="my-submissions-card">
        <div className="flex items-center gap-2.5">
          <FileCheck className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold font-heading text-slate-900">My Submissions</h2>
        </div>
        {!subs ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : subs.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500" data-testid="submissions-empty">No submissions yet. Head to Tasks to complete your first one.</p>
        ) : (
          <div className="mt-4 space-y-2.5" data-testid="submissions-list">
            {subs.map((s) => (
              <button key={s.id} onClick={() => setDetail(s)} data-testid={`submission-card-${s.submission_code}`}
                className="w-full text-left flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3.5 hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{s.task_name}</p>
                  <p className="text-xs text-slate-500">{s.submission_code} · {fmtDate(s.created_at)} · {inr(s.reward)}</p>
                </div>
                <StatusBadge status={s.status} testid={`submission-status-${s.submission_code}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={savePassword} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-7 space-y-4" data-testid="password-card">
        <div className="flex items-center gap-2.5">
          <KeyRound className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold font-heading text-slate-900">Change Password</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Current password</label>
            <input data-testid="profile-current-password" type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
            <input data-testid="profile-new-password" type="password" required minLength={6} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inCls} />
          </div>
        </div>
        <button type="submit" data-testid="password-save-btn" disabled={savingPw}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-60 transition-colors">
          {savingPw && <Loader2 className="w-4 h-4 animate-spin" />} Update Password
        </button>
      </form>

      <button onClick={doLogout} data-testid="account-logout-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors">
        <LogOut className="w-4 h-4" /> Logout
      </button>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="submission-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3 flex-wrap">{detail.task_name} <StatusBadge status={detail.status} /></DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-1 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3.5">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Submission ID</p>
                    <p className="mt-1 font-semibold text-slate-900" data-testid="detail-submission-id">{detail.submission_code}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3.5">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Submitted</p>
                    <p className="mt-1 font-semibold text-slate-900">{fmtDate(detail.created_at)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3.5">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Task</p>
                    <p className="mt-1 font-semibold text-slate-900">{detail.task_code}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3.5">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Reward</p>
                    <p className="mt-1 font-semibold text-blue-700">{inr(detail.reward)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Your feedback</p>
                  <p className="mt-1.5 text-slate-700 leading-relaxed bg-white border border-slate-200 rounded-xl p-4" data-testid="detail-feedback">{detail.feedback}</p>
                  {detail.comments && <p className="mt-2 text-xs text-slate-500 italic">Comments: {detail.comments}</p>}
                </div>
                {detail.screenshot_path && (
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Screenshot / Proof</p>
                    <a href={fileUrl(detail.screenshot_path)} target="_blank" rel="noopener noreferrer">
                      <img src={fileUrl(detail.screenshot_path)} alt="Proof" data-testid="detail-screenshot" className="mt-1.5 max-h-64 rounded-xl border border-slate-200 object-contain bg-slate-50" />
                    </a>
                  </div>
                )}
                {detail.status === "rejected" && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="detail-rejection-box">
                    <p className="font-semibold text-red-800">Rejection reason</p>
                    <p className="text-red-700 mt-1">{detail.rejection_reason}</p>
                  </div>
                )}
                {detail.status === "verified" && <p className="text-xs text-green-700 font-medium">Verified on {fmtDate(detail.verified_at)} · Reward credited to your wallet</p>}
                {detail.status === "pending" && <p className="text-xs text-amber-700 font-medium">Waiting for admin verification (usually 24–72 hours)</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
